/**
 * Pure sky-lighting math (Graphics G3) — no THREE renderer state, Node-unit-testable.
 *
 * Maps `SkyState.timeOfDay` (0..1) to a lighting band + cross-fade, and the solar
 * arc. The bands ring the day: night(0) → dawn(0.25) → day(0.5) → dusk(0.75) → night.
 */
import * as THREE from 'three/webgpu';

export type Band = 'dawn' | 'day' | 'dusk' | 'night';

/** Band order around the day (each spans a 0.25 segment of timeOfDay). */
const ORDER: Band[] = ['night', 'dawn', 'day', 'dusk'];

/** The active band, the band being crossed into, and a 0..1 cross-fade factor. */
export function bandForTimeOfDay(tod: number): { band: Band; next: Band; blend: number } {
  const t = ((tod % 1) + 1) % 1; // wrap to [0,1)
  const seg = Math.min(3, Math.floor(t / 0.25)); // 0..3 → night/dawn/day/dusk segment
  return { band: ORDER[seg], next: ORDER[(seg + 1) % 4], blend: (t - seg * 0.25) / 0.25 };
}

/** Solar elevation sin in [-1, 1] (negative = the sun is below the horizon = night). */
export function sunElevation(tod: number): number {
  return Math.sin(tod * Math.PI * 2 - Math.PI / 2); // −1 midnight, 0 dawn/dusk, +1 noon
}

/** World position for the sun DirectionalLight along its day arc (light points at the origin).
 *  Pass `out` from per-frame callers to skip the Vector3 allocation. */
export function sunPosition(tod: number, dist = 120, out?: THREE.Vector3): THREE.Vector3 {
  const dayAngle = tod * Math.PI * 2 - Math.PI / 2;
  const el = Math.sin(dayAngle);                 // height: −1 midnight → +1 noon
  const az = tod * Math.PI * 2;                   // azimuth sweep (east → west)
  const horiz = Math.max(0.12, Math.cos(dayAngle)); // horizontal radius (≈overhead at noon)
  return (out ?? new THREE.Vector3()).set(Math.cos(az) * horiz * dist, el * dist, Math.sin(az) * horiz * dist);
}

/** Linear interpolate two sRGB-hex colors → a THREE.Color (linear). */
export function lerpColorHex(aHex: string, bHex: string, t: number): THREE.Color {
  return lerpColorHexInto(aHex, bHex, t, new THREE.Color());
}

/** Allocation-free lerpColorHex — per-frame callers pass their own scratch. */
export function lerpColorHexInto(aHex: string, bHex: string, t: number, out: THREE.Color): THREE.Color {
  out.set(aHex);
  _lerpScratch.set(bHex);
  return out.lerp(_lerpScratch, THREE.MathUtils.clamp(t, 0, 1));
}
const _lerpScratch = new THREE.Color();

// ── Day/night driver (Phase B keystone) ─────────────────────────────────────
// Pure functions consumed by Tasks 3-6.  sunY = sin(elevation) = sunDirection.y.
// Research twilight anchors: civil dusk ≈ +0.04, nautical dusk ≈ -0.07,
// astronomical dusk ≈ -0.16; milky way visible only in astronomical dark ≈ -0.28.

/** Smoothstep (Hermite) clamped to [0, 1]. */
function smooth(t: number): number { const x = Math.max(0, Math.min(1, t)); return x * x * (3 - 2 * x); }

/** Normalized unit direction FROM origin TO the sun, as a plain {x,y,z} object.
 *  The returned literal stays fresh (callers may retain it); the two INNER
 *  Vector3s this used to allocate per call now reuse module scratch. */
const _sunDirScratch = new THREE.Vector3();
export function sunDirection(tod: number): { x: number; y: number; z: number } {
  const v = sunPosition(tod, 120, _sunDirScratch).normalize();
  return { x: v.x, y: v.y, z: v.z };
}

/**
 * 0 when the sun is clearly up, 1 when clearly below the horizon.
 * Ramps via smoothstep from civil twilight (+0.04) down through nautical (-0.16).
 */
export function nightFactor(sunY: number): number { return smooth((0.04 - sunY) / (0.04 - -0.16)); }

/**
 * The FLAT sky's fallback colour (minimal tier, `SkyModule.initFlat`) when no
 * atmosphere fog colour is available: the day-band haze at noon, the night
 * band at midnight, by the night factor. Pure — the harness pins the ramp.
 */
export const FLAT_SKY_DAY_HEX = '#accfe6';
export const FLAT_SKY_NIGHT_HEX = '#161e36';
export function flatSkyFallbackColor(nightFactor01: number, out: THREE.Color): THREE.Color {
  return lerpColorHexInto(FLAT_SKY_DAY_HEX, FLAT_SKY_NIGHT_HEX, nightFactor01, out);
}

/**
 * Lags nightFactor — milky way / nebula visible only in astronomical darkness.
 * Ramps from nautical twilight (-0.10) down through astronomical (-0.28).
 */
export function deepNight(sunY: number): number { return smooth((-0.10 - sunY) / (-0.10 - -0.28)); }

/**
 * Step a time-of-day value (0..1) by `delta`, wrapping into [0,1). Used by the
 * day/night preview keys + the debug menu to nudge the Local time override
 * (0.0 = midnight, 0.5 = noon). The double-mod handles negative deltas.
 */
export function stepTod(t: number, delta: number): number {
  return ((t + delta) % 1 + 1) % 1;
}
